/**
 * Placement drives, batch by batch, from the Training & Placement Cell's own
 * sheet "Placement Details - 2025 & 2026":
 * https://docs.google.com/spreadsheets/d/1Tlf0jGa5fAgh-7D8MVuZl3slz3su0CJh9ukcOAj6UUo/edit
 *
 * One row per drive, reproduced as recorded. Every figure below reconciles
 * against the sheet's own TOTAL row: for both batches the sum of `selected`,
 * the sum of all per-department cells, and male+female all agree with it.
 *
 * Department codes are translated to the ones departments.ts uses:
 *   IX -> TX
 * (The sheet's own "AI" and "CB" already match departments.ts's codes for
 * Artificial Intelligence & Machine Learning and Computer Science &
 * Business Systems — both renamed from "AIML"/"CSBS" 2026-08-10 to
 * converge with staff.ts's staffCode, so no translation is needed there.)
 * `IX` is the 2026 sheet's code for Textile: its only three employers are
 * Jockey, Shahi Exports and Aravind, all of which appear under `TX` in the 2025
 * sheet, and `TX` is absent from the 2026 columns.
 *
 * Three things the source leaves incomplete, carried rather than papered over:
 *
 *   1. `ctc` is null where the sheet prints "CSR" instead of a figure — 13 of
 *      the 2026 drives and 8 of the 2025 ones. Those are training providers;
 *      the sheet states no package for them, so neither do we. `ctcNote` keeps
 *      the literal value.
 *   2. The 2025 batch attributes only 552 of its 579 offers to a gender; 18
 *      rows (mostly the unnamed-date block at the end) have neither figure. Use
 *      GENDER_ATTRIBUTED, not the batch total, as the denominator for any
 *      gender split.
 *   3. One 2026 drive (20/11/2025, 50 offers) has no company name in the
 *      source. It is kept because its offers are inside the sheet's totals.
 *
 * Dates are the source's own strings. A few are visibly mistyped — the two
 * Cognizant GenC sub-drives read 24/10/2026 and 24/10/2027 where the parent row
 * reads 24/10/2025 — so they are not parsed into Date objects here.
 */

/** Departments that appear in either sheet, in departments.ts codes. */
export type PlacementDeptCode =
  | "AI" | "BT" | "CG" | "CH" | "CS" | "CB" | "CV"
  | "CD" | "EC" | "EE" | "IS" | "MCA" | "ME" | "TX";

export interface PlacementDrive {
  /** As printed in the sheet; see the note on mistyped dates above. */
  date: string;
  /** Empty for the one 2026 row the sheet leaves unnamed. */
  company: string;
  selected: number;
  /** Offers by department — only departments with a number are present. */
  byDept: Partial<Record<PlacementDeptCode, number>>;
  /** LPA, or null where the sheet prints something other than a figure. */
  ctc: number | null;
  /** The literal cell when it is not a number, e.g. "CSR". */
  ctcNote?: string;
  male: number;
  female: number;
}

export interface PlacementBatch {
  /** Graduating batch, e.g. "2026". */
  batch: string;
  /** The sheet tab this came from. */
  gid: number;
  drives: PlacementDrive[];
  /** The sheet's own TOTAL row. */
  totalSelected: number;
  male: number;
  female: number;
  /** Verbatim from the TOTAL row's CTC cell. */
  summary: string;
}

export const PLACEMENT_BATCHES: PlacementBatch[] = [
  {
    batch: "2026",
    gid: 1702639827,
    totalSelected: 596,
    male: 243,
    female: 353,
    summary: "Average Salary - 5.54 LPA Median Salary - 4.5 LPA Highest Package - 40 LPA",
    drives: [
      { date: "9/7/2025", company: "LearnFlu", selected: 11, byDept: { CS: 6, EC: 1, IS: 4 }, ctc: 6.0, male: 5, female: 6 },
      { date: "25/8/2025", company: "ZOSH Aerospace", selected: 2, byDept: { EC: 2 }, ctc: 5.5, male: 0, female: 2 },
      { date: "2/9/2025", company: "Toshiba", selected: 4, byDept: { CS: 2, EC: 1, IS: 1 }, ctc: 7.5, male: 3, female: 1 },
      { date: "30/9/2025", company: "IBM", selected: 7, byDept: { CS: 2, CB: 2, CD: 3 }, ctc: 4.5, male: 3, female: 4 },
      { date: "14/10/2025", company: "Ocean Link Sea Service", selected: 17, byDept: { EC: 12, EE: 4, ME: 1 }, ctc: 6.0, male: 17, female: 0 },
      { date: "18/10/2025", company: "Pragathi Prerana Engg Works", selected: 2, byDept: { EC: 2 }, ctc: 7.0, male: 2, female: 0 },
      { date: "22/10/2025", company: "iVoyant", selected: 3, byDept: { CS: 2, IS: 1 }, ctc: 4.2, male: 1, female: 2 },
      { date: "24/10/2025", company: "Cognizant GenC", selected: 38, byDept: { AI: 1, CG: 3, CS: 16, CB: 2, CD: 4, EC: 2, IS: 10 }, ctc: 4.0, male: 23, female: 15 },
      { date: "24/10/2026", company: "Cognizant GenC Next", selected: 2, byDept: { CS: 2 }, ctc: 6.75, male: 2, female: 0 },
      { date: "24/10/2027", company: "Cognizant GenC Pro", selected: 1, byDept: { AI: 1 }, ctc: 5.4, male: 0, female: 1 },
      { date: "30/10/2025", company: "Synthite Industries (P) Ltd", selected: 2, byDept: { CH: 2 }, ctc: 4.0, male: 1, female: 1 },
      { date: "30/10/2025", company: "Coditas", selected: 1, byDept: { CS: 1 }, ctc: 4.5, male: 1, female: 0 },
      { date: "31/10/2025", company: "Ampcus Cyber India", selected: 2, byDept: { CG: 1, CB: 1 }, ctc: 4.5, male: 0, female: 2 },
      { date: "6/11/2025", company: "TAP Academy", selected: 24, byDept: { AI: 4, CS: 8, CB: 2, CD: 2, EC: 3, IS: 5 }, ctc: null, ctcNote: "CSR", male: 6, female: 18 },
      { date: "7/11/2025", company: "X-Workz", selected: 31, byDept: { AI: 6, CS: 10, CB: 5, CD: 2, EC: 2, IS: 6 }, ctc: null, ctcNote: "CSR", male: 11, female: 20 },
      { date: "11/11/2025", company: "Q Spiders", selected: 25, byDept: { AI: 3, CG: 3, CS: 9, CD: 1, EE: 1, IS: 8 }, ctc: null, ctcNote: "CSR", male: 11, female: 14 },
      { date: "20/11/2025", company: "", selected: 50, byDept: { AI: 4, CG: 5, CS: 13, CB: 3, CD: 7, EC: 3, EE: 1, IS: 13, MCA: 1 }, ctc: null, ctcNote: "CSR", male: 25, female: 25 },
      { date: "14/11/2025", company: "Infosys", selected: 10, byDept: { CG: 1, CS: 4, CB: 1, EC: 2, IS: 2 }, ctc: 3.6, male: 0, female: 10 },
      { date: "17/11/2025", company: "Vstand4u", selected: 2, byDept: { IS: 1, MCA: 1 }, ctc: null, ctcNote: "CSR", male: 0, female: 2 },
      { date: "21/11/2025", company: "Rinex Technologies", selected: 16, byDept: { CG: 4, CB: 1, EC: 1, EE: 2, IS: 8 }, ctc: 5.0, male: 2, female: 14 },
      { date: "25/11/2025", company: "Dextris / Echo Brains", selected: 8, byDept: { CS: 2, CB: 1, CD: 1, EC: 1, EE: 1, IS: 2 }, ctc: null, ctcNote: "CSR", male: 4, female: 4 },
      { date: "27/11/2025", company: "Dhee Coding Lab", selected: 20, byDept: { CG: 3, CS: 7, CB: 1, IS: 8, MCA: 1 }, ctc: null, ctcNote: "CSR", male: 7, female: 13 },
      { date: "1/12/2025", company: "Pentagone Space", selected: 29, byDept: { AI: 3, CG: 2, CS: 13, CB: 1, CD: 1, EE: 1, IS: 6, MCA: 2 }, ctc: null, ctcNote: "CSR", male: 5, female: 24 },
      { date: "2/12/2025", company: "iNube", selected: 8, byDept: { CS: 3, CD: 1, IS: 4 }, ctc: 6.5, male: 1, female: 7 },
      { date: "10/12/2025", company: "Cleo", selected: 3, byDept: { CS: 3 }, ctc: 7.0, male: 2, female: 1 },
      { date: "10/12/2025", company: "Logic brackets", selected: 5, byDept: { CS: 5 }, ctc: 2.8, male: 5, female: 0 },
      { date: "12/12/2025", company: "Heartiest Mind Technologies", selected: 9, byDept: { AI: 2, CS: 4, CD: 1, IS: 2 }, ctc: 6.0, male: 3, female: 6 },
      { date: "16/12/2025", company: "SANRadiance Technologies (Off Campus)", selected: 2, byDept: { CS: 2 }, ctc: 3.0, male: 2, female: 0 },
      { date: "16/12/2025", company: "Global Quest Technologies", selected: 27, byDept: { CG: 2, CS: 10, CB: 2, CD: 3, EC: 3, EE: 1, IS: 6 }, ctc: null, ctcNote: "CSR", male: 6, female: 21 },
      { date: "16/12/2025", company: "VTech Integrated Solutions", selected: 32, byDept: { CG: 1, CS: 14, CB: 1, CD: 1, IS: 10, MCA: 5 }, ctc: null, ctcNote: "CSR", male: 7, female: 25 },
      { date: "19/12/2025", company: "MuSigma", selected: 7, byDept: { AI: 1, CG: 1, CS: 2, CD: 1, IS: 2 }, ctc: 40.0, male: 3, female: 4 },
      { date: "22/12/2025", company: "Tech Fortune", selected: 2, byDept: { CS: 1, IS: 1 }, ctc: null, ctcNote: "CSR", male: 1, female: 1 },
      { date: "23/12/2025", company: "Delphi-TVS Technologies Limited", selected: 6, byDept: { EC: 6 }, ctc: 2.04, male: 0, female: 6 },
      { date: "5/1/2026", company: "TwoK", selected: 1, byDept: { CH: 1 }, ctc: 2.16, male: 1, female: 0 },
      { date: "24/1/2026", company: "Rinex Technologies", selected: 1, byDept: { CV: 1 }, ctc: 5.0, male: 1, female: 0 },
      { date: "27/1/2026", company: "Allegis Group", selected: 1, byDept: { ME: 1 }, ctc: 2.4, male: 1, female: 0 },
      { date: "4/2/2026", company: "TCS-Ninja", selected: 48, byDept: { AI: 4, CG: 3, CS: 19, CB: 4, CD: 8, EC: 4, IS: 6 }, ctc: 3.46, male: 21, female: 27 },
      { date: "", company: "TCS-Digital", selected: 9, byDept: { AI: 1, CS: 7, CD: 1 }, ctc: 7.09, male: 4, female: 5 },
      { date: "", company: "TCS-Prime", selected: 2, byDept: { CS: 2 }, ctc: 9.09, male: 2, female: 0 },
      { date: "9/2/2026", company: "Jockey", selected: 11, byDept: { TX: 11 }, ctc: 3.0, male: 3, female: 8 },
      { date: "10/2/2026", company: "NXTSYNC Pvt. Ltd", selected: 17, byDept: { AI: 1, CS: 3, CB: 3, CD: 4, EC: 6 }, ctc: 5.5, male: 8, female: 9 },
      { date: "13/2/2026", company: "Dyashin DSEdify", selected: 5, byDept: { AI: 1, CG: 1, CS: 1, CB: 1, MCA: 1 }, ctc: 4.0, male: 1, female: 4 },
      { date: "25/2/2026", company: "PRDC", selected: 5, byDept: { EE: 5 }, ctc: 3.6, male: 5, female: 0 },
      { date: "6/3/2026", company: "ASC International", selected: 4, byDept: { CV: 2, EE: 2 }, ctc: 6.0, male: 1, female: 3 },
      { date: "17/3/2026", company: "RDC Concrete", selected: 1, byDept: { CV: 1 }, ctc: 3.6, male: 1, female: 0 },
      { date: "31/3/2026", company: "Blanco Steel Detailing", selected: 15, byDept: { CV: 12, ME: 3 }, ctc: 2.6, male: 14, female: 1 },
      { date: "28/4/2026", company: "Shahi Exports", selected: 2, byDept: { TX: 2 }, ctc: 3.6, male: 1, female: 1 },
      { date: "4/4/2026", company: "Intrnforte", selected: 3, byDept: { EC: 2, EE: 1 }, ctc: 5.0, male: 1, female: 2 },
      { date: "8/5/2026", company: "Elytrhra Edufi Tech Solutions", selected: 11, byDept: { CV: 1, EC: 7, EE: 2, IS: 1 }, ctc: 7.0, male: 4, female: 7 },
      { date: "8/5/2026", company: "Learnflu", selected: 3, byDept: { CS: 1, CD: 1, MCA: 1 }, ctc: 3.0, male: 0, female: 3 },
      { date: "15/5/2026", company: "Emmvee Energy Pvt.Ltd", selected: 16, byDept: { EC: 8, EE: 6, ME: 2 }, ctc: 2.64, male: 9, female: 7 },
      { date: "18/5/2026", company: "Aravind", selected: 2, byDept: { TX: 2 }, ctc: 3.8, male: 1, female: 1 },
      { date: "18/5/2026", company: "Averixis", selected: 17, byDept: { CS: 7, CD: 1, EC: 8, IS: 1 }, ctc: 4.0, male: 3, female: 14 },
      { date: "4/6/2026", company: "InternSpot", selected: 14, byDept: { CS: 3, EC: 3, IS: 8 }, ctc: 6.0, male: 2, female: 12 },
    ],
  },
  {
    batch: "2025",
    gid: 13197804,
    totalSelected: 579,
    male: 235,
    female: 317,
    summary: "Average Salary-4.4 LPA Median Salary-4.0 LPA Highest Package -10 LPA",
    drives: [
      { date: "11/9/2024", company: "Cognizant", selected: 14, byDept: { CS: 8, EC: 1, IS: 5 }, ctc: 4.0, male: 10, female: 4 },
      { date: "17/9/2024", company: "Skill Forge", selected: 3, byDept: { MCA: 3 }, ctc: 4.08, male: 1, female: 2 },
      { date: "13/11/2024", company: "Zepto", selected: 2, byDept: { EC: 1, IS: 1 }, ctc: 4.5, male: 2, female: 0 },
      { date: "5/10/2024", company: "TCS-Ninja", selected: 71, byDept: { CS: 22, CV: 1, EC: 10, EE: 2, IS: 34, MCA: 2 }, ctc: 3.3, male: 23, female: 48 },
      { date: "5/10/2024", company: "TCS-Digital", selected: 3, byDept: { IS: 3 }, ctc: 7.0, male: 2, female: 1 },
      { date: "27/11/2024", company: "Tap Academy", selected: 14, byDept: { CS: 6, EC: 2, IS: 3, MCA: 3 }, ctc: null, ctcNote: "CSR", male: 5, female: 9 },
      { date: "14/12/2024", company: "Dheecoding Labs", selected: 12, byDept: { CS: 5, IS: 6, MCA: 1 }, ctc: null, ctcNote: "CSR", male: 3, female: 9 },
      { date: "24-10-2024 & 27-11-2024", company: "Prerana Engineering Works", selected: 3, byDept: { EC: 1, IS: 2 }, ctc: 5.0, male: 1, female: 2 },
      { date: "2-12-2024 & 16-12-2024", company: "KPMG", selected: 15, byDept: { CS: 10, IS: 5 }, ctc: 5.0, male: 4, female: 11 },
      { date: "21/11/2024", company: "Aarbee Structures Pvt.Ltd", selected: 1, byDept: { CV: 1 }, ctc: 3.0, male: 1, female: 0 },
      { date: "18/12/2024", company: "CodeYoung", selected: 12, byDept: { CS: 7, EE: 1, IS: 4 }, ctc: 4.36, male: 12, female: 0 },
      { date: "13/12/2024", company: "Zosh", selected: 2, byDept: { CS: 1, EC: 1 }, ctc: 5.5, male: 1, female: 1 },
      { date: "20-12-2024 & 21-12-2024", company: "Kasmo", selected: 23, byDept: { CS: 12, IS: 9, ME: 2 }, ctc: 3.5, male: 10, female: 13 },
      { date: "23-12-2024 & 24-12-2024", company: "Infosys Ltd.", selected: 4, byDept: { CS: 1, EC: 2, IS: 1 }, ctc: 3.6, male: 0, female: 4 },
      { date: "12/12/2024", company: "IBM", selected: 1, byDept: { CS: 1 }, ctc: 8.0, male: 0, female: 1 },
      { date: "9/12/2024", company: "Sasken", selected: 1, byDept: { IS: 1 }, ctc: 5.0, male: 0, female: 1 },
      { date: "30/1/2025", company: "X-Ciencia", selected: 4, byDept: { CS: 2, IS: 2 }, ctc: 4.0, male: 3, female: 1 },
      { date: "23/11/2025", company: "Palle Technologies", selected: 32, byDept: { CS: 15, IS: 17 }, ctc: null, ctcNote: "CSR", male: 5, female: 27 },
      { date: "10/2/2025", company: "Unisur Lifecare Pvt Ltd", selected: 1, byDept: { TX: 1 }, ctc: 3.5, male: 1, female: 0 },
      { date: "2/12/2024", company: "Baldota Group", selected: 6, byDept: { CV: 2, EE: 2, ME: 2 }, ctc: 3.2, male: 4, female: 2 },
      { date: "28/1/2025", company: "M/s. Kalpataru Projects International Ltd.", selected: 3, byDept: { CV: 3 }, ctc: 4.5, male: 3, female: 0 },
      { date: "13/2/2025", company: "IBM", selected: 11, byDept: { CS: 7, EC: 1, IS: 2, MCA: 1 }, ctc: 4.5, male: 5, female: 6 },
      { date: "16/6/2025", company: "IBM", selected: 1, byDept: { CS: 1 }, ctc: 8.5, male: 0, female: 1 },
      { date: "15/2/2025", company: "QSpiders", selected: 21, byDept: { CS: 9, CV: 1, IS: 11 }, ctc: null, ctcNote: "CSR", male: 4, female: 17 },
      { date: "20/2/2025", company: "Pentagon Space", selected: 15, byDept: { CS: 8, IS: 6, MCA: 1 }, ctc: null, ctcNote: "CSR", male: 2, female: 13 },
      { date: "5/3/2025", company: "INube Software Solutions", selected: 2, byDept: { CS: 1, IS: 1 }, ctc: 4.0, male: 2, female: 0 },
      { date: "6/3/2025", company: "UHP Technologies", selected: 1, byDept: { CH: 1 }, ctc: 4.0, male: 0, female: 1 },
      { date: "10/3/2025", company: "SkyllX Technologies Private Limited", selected: 12, byDept: { CS: 6, EC: 2, IS: 3, MCA: 1 }, ctc: null, ctcNote: "CSR", male: 3, female: 9 },
      { date: "12/3/2025", company: "DMG MORI INDIA", selected: 3, byDept: { ME: 3 }, ctc: 3.0, male: 2, female: 1 },
      { date: "14/3/2025", company: "RDC Concrete", selected: 2, byDept: { CV: 2 }, ctc: 3.6, male: 2, female: 0 },
      { date: "22/3/2025", company: "QSpiders", selected: 45, byDept: { CS: 17, EC: 8, IS: 14, MCA: 6 }, ctc: null, ctcNote: "CSR", male: 26, female: 19 },
      { date: "28/3/2025", company: "MOTHERSON", selected: 4, byDept: { EC: 4 }, ctc: 2.76, male: 4, female: 0 },
      { date: "28/3/2025", company: "TechMahindra", selected: 35, byDept: { CS: 13, IS: 6, MCA: 16 }, ctc: 3.0, male: 13, female: 22 },
      { date: "29/3/2025", company: "Lucas TVS", selected: 3, byDept: { CV: 3 }, ctc: 2.4, male: 3, female: 0 },
      { date: "29/3/2025", company: "ThinkSync", selected: 14, byDept: { CS: 6, MCA: 8 }, ctc: 2.4, male: 5, female: 9 },
      { date: "1/4/2025", company: "NinjaCart", selected: 1, byDept: { CS: 1 }, ctc: 3.1, male: 1, female: 0 },
      { date: "8/4/2025", company: "Echo Brains", selected: 23, byDept: { CS: 8, EC: 7, IS: 7, ME: 1 }, ctc: null, ctcNote: "CSR", male: 6, female: 17 },
      { date: "12/4/2025", company: "Friss India", selected: 16, byDept: { CS: 6, CV: 1, IS: 8, ME: 1 }, ctc: 5.0, male: 7, female: 9 },
      { date: "14/4/2025", company: "Hashedin Technologies", selected: 2, byDept: { IS: 2 }, ctc: 8.1, male: 1, female: 1 },
      { date: "10/4/2025", company: "Genz Educate Wing", selected: 9, byDept: { CS: 8, EE: 1 }, ctc: 5.0, male: 5, female: 4 },
      { date: "19/4/2025", company: "Intrainz", selected: 42, byDept: { CS: 9, CV: 3, EC: 16, IS: 12, MCA: 2 }, ctc: 6.0, male: 19, female: 23 },
      { date: "24/4/2025", company: "Mahanagara Gas Limited", selected: 3, byDept: { ME: 3 }, ctc: 4.2, male: 2, female: 1 },
      { date: "9/3/2025", company: "Ingest Labs", selected: 2, byDept: { CS: 2 }, ctc: 3.0, male: 2, female: 0 },
      { date: "11/3/2025", company: "Coditas", selected: 1, byDept: { CS: 1 }, ctc: 4.5, male: 1, female: 0 },
      { date: "22/5/2025", company: "Indo-MIM", selected: 1, byDept: { ME: 1 }, ctc: 3.63, male: 1, female: 0 },
      { date: "3/6/2025", company: "Corizo", selected: 2, byDept: { CS: 1, IS: 1 }, ctc: 4.0, male: 0, female: 2 },
      { date: "22/6/2025", company: "Cognizant", selected: 2, byDept: { CS: 1, IS: 1 }, ctc: 4.0, male: 0, female: 2 },
      { date: "10/6/2025", company: "Kaynes Technology", selected: 7, byDept: { EC: 5, EE: 2 }, ctc: 2.64, male: 4, female: 3 },
      { date: "23/6/2025", company: "Rinex Technologies", selected: 5, byDept: { CS: 3, EC: 1, IS: 1 }, ctc: 10.0, male: 3, female: 2 },
      { date: "16/6/2025", company: "Internz Valley", selected: 2, byDept: { CS: 1, EC: 1 }, ctc: 5.0, male: 1, female: 1 },
      { date: "9/6/2025", company: "CuraTeQ Biologics", selected: 9, byDept: { BT: 2, CH: 3, ME: 4 }, ctc: 3.6, male: 6, female: 3 },
      { date: "8/7/2025", company: "LearnFlu", selected: 7, byDept: { CS: 4, IS: 3 }, ctc: 6.0, male: 3, female: 4 },
      { date: "10/7/2025", company: "Shahi Exports", selected: 10, byDept: { TX: 10 }, ctc: 3.6, male: 6, female: 4 },
      { date: "14/7/2025", company: "Vishwaa Apparels", selected: 2, byDept: { TX: 2 }, ctc: 3.0, male: 0, female: 2 },
      { date: "17/7/2025", company: "Wonderblues Apparels", selected: 1, byDept: { TX: 1 }, ctc: 3.0, male: 1, female: 0 },
      { date: "18/7/2025", company: "Avantara Tailor Mart", selected: 1, byDept: { TX: 1 }, ctc: 3.0, male: 0, female: 1 },
      { date: "21/7/2025", company: "Silver Crest Clothing", selected: 1, byDept: { TX: 1 }, ctc: 3.0, male: 0, female: 1 },
      { date: "20/8/2025", company: "Odyessus Solution", selected: 3, byDept: { CS: 1, IS: 2 }, ctc: 3.0, male: 2, female: 1 },
      { date: "16/10/2025", company: "Rupeek", selected: 4, byDept: { MCA: 4 }, ctc: 3.0, male: 2, female: 2 },
      { date: "", company: "Indus Towers", selected: 1, byDept: { ME: 1 }, ctc: 3.5, male: 0, female: 0 },
      { date: "", company: "Sobha", selected: 1, byDept: { CH: 1 }, ctc: 3.0, male: 0, female: 0 },
      { date: "", company: "Petrichor Solar Pvt. Ltd, Banglore", selected: 1, byDept: { CH: 1 }, ctc: 2.5, male: 0, female: 0 },
      { date: "", company: "VMX HI- Connectors Pvt. Ltd. Banglore", selected: 1, byDept: { CH: 1 }, ctc: 3.5, male: 0, female: 0 },
      { date: "", company: "K-pack Systems Private Limited", selected: 1, byDept: { CH: 1 }, ctc: 3.0, male: 0, female: 0 },
      { date: "", company: "PSK Pharma Pvt. Ltd.", selected: 1, byDept: { CH: 1 }, ctc: 3.5, male: 0, female: 0 },
      { date: "", company: "R L Fine Chem Pvt. Ltd. Banglore", selected: 3, byDept: { CH: 3 }, ctc: 4.0, male: 0, female: 0 },
      { date: "", company: "VMX HI- Connectors Pvt. Ltd.", selected: 1, byDept: { CH: 1 }, ctc: 4.0, male: 0, female: 0 },
      { date: "", company: "Cargil India Pvt. Ltd.", selected: 1, byDept: { CH: 1 }, ctc: 4.0, male: 0, female: 0 },
      { date: "", company: "Bharath Electronics Ltd. Banglore", selected: 1, byDept: { CH: 1 }, ctc: 3.6, male: 0, female: 0 },
      { date: "", company: "LM Wind Power, Banglore", selected: 1, byDept: { CH: 1 }, ctc: 6.0, male: 0, female: 0 },
      { date: "", company: "Bastian- India Conveyor", selected: 1, byDept: { ME: 1 }, ctc: 3.0, male: 0, female: 0 },
      { date: "", company: "BMM Ispat Limited", selected: 1, byDept: { ME: 1 }, ctc: 2.5, male: 0, female: 0 },
      { date: "", company: "Maini Precision products Ltd.", selected: 7, byDept: { ME: 7 }, ctc: 3.0, male: 0, female: 0 },
      { date: "", company: "Pragathi Polymers", selected: 1, byDept: { ME: 1 }, ctc: 3.0, male: 0, female: 0 },
      { date: "", company: "Arvind Mill", selected: 2, byDept: { TX: 2 }, ctc: 3.0, male: 0, female: 0 },
      { date: "", company: "Boeing (off Campus)", selected: 1, byDept: { TX: 1 }, ctc: 3.0, male: 0, female: 0 },
      { date: "", company: "Jockey India", selected: 1, byDept: { TX: 1 }, ctc: 3.0, male: 0, female: 0 },
    ],
  },
];

/** The batch a page should lead with. */
export const LATEST_BATCH = PLACEMENT_BATCHES[0];

/** Offers per department for one batch, highest first. */
export function deptTotals(b: PlacementBatch): Array<{ code: PlacementDeptCode; offers: number }> {
  const m = new Map<PlacementDeptCode, number>();
  for (const d of b.drives) {
    for (const [code, n] of Object.entries(d.byDept)) {
      m.set(code as PlacementDeptCode, (m.get(code as PlacementDeptCode) ?? 0) + (n ?? 0));
    }
  }
  return [...m.entries()]
    .map(([code, offers]) => ({ code, offers }))
    .sort((a, z) => z.offers - a.offers);
}

/**
 * Employers for one batch, by offers made, highest first. Drives are merged on
 * company name because several firms ran more than one — Cognizant, IBM,
 * QSpiders and Rinex all appear repeatedly — and the unnamed 2026 row is
 * dropped, since it cannot be attributed to an employer.
 */
export function recruiterTotals(b: PlacementBatch): Array<{ company: string; offers: number; drives: number }> {
  const m = new Map<string, { offers: number; drives: number }>();
  for (const d of b.drives) {
    if (!d.company) continue;
    const cur = m.get(d.company) ?? { offers: 0, drives: 0 };
    m.set(d.company, { offers: cur.offers + d.selected, drives: cur.drives + 1 });
  }
  return [...m.entries()]
    .map(([company, v]) => ({ company, ...v }))
    .sort((a, z) => z.offers - a.offers || a.company.localeCompare(z.company));
}

/** Companies that recruited in this batch, counting one per employer. */
export const employerCount = (b: PlacementBatch) => recruiterTotals(b).length;

/**
 * Offers that carry a gender. The 2025 sheet leaves 27 of its 579 unattributed,
 * so this — not totalSelected — is the denominator for a gender split.
 */
export const genderAttributed = (b: PlacementBatch) => b.male + b.female;

/** The highest package the sheet states for a batch, in LPA. */
export function highestCtc(b: PlacementBatch): number {
  return b.drives.reduce((max, d) => (d.ctc && d.ctc > max ? d.ctc : max), 0);
}

/**
 * Average / median / highest, as the sheet's own TOTAL-row summary states
 * them in prose (`summary`) — not recomputed, since the sheet's own average
 * may weight drives in a way `highestCtc`-style aggregation over `drives`
 * would not reproduce. Any figure the summary doesn't state comes back null.
 */
export function summaryFigures(b: PlacementBatch) {
  const grab = (re: RegExp) => b.summary.match(re)?.[1] ?? null;
  return {
    average: grab(/Average Salary\s*-?\s*([\d.]+)/i),
    median: grab(/Median Salary\s*-?\s*([\d.]+)/i),
    highest: grab(/Highest Package\s*-?\s*([\d.]+)/i),
  };
}

/* ------------------------------------------------------------------------- *
 * The second source: each department's own page
 * ------------------------------------------------------------------------- *
 *
 * Nine engineering departments plus MCA publish a year-by-year placement table
 * on their own page; those tables are already in departments.ts, and are read
 * from there rather than copied. DS and CB publish a company-wise table
 * instead, and AI, CD and the basic sciences publish none.
 *
 * THESE DO NOT AGREE WITH THE BATCH SHEET ABOVE, and must not be added to it:
 *
 *   code   dept page 2024-25    T&P sheet 2025 batch
 *   CS              47                    204
 *   IS              69                    173
 *   MCA             73                     48
 *
 * They are counting different things. The sheet counts a *graduating batch* —
 * its 2025-batch drives run from Sept 2024 to Oct 2025, straddling two academic
 * years — while a department table counts an *academic year*. On top of that the
 * department pages are updated by each department at its own pace, so several
 * stop at 2023-24 while others carry a part-finished 2025-26 row.
 *
 * Where the two can be compared directly they corroborate each other: the CB
 * page's 2026 column sums to 31, exactly the CB total in the 2026 sheet. So
 * the divergence is one of definition and freshness, not of contradictory
 * record-keeping. Use the sheet for batch numbers and these for a department's
 * own long-run history; never mix them in one figure.
 */

export interface DeptPlacementYear {
  /** departments.ts code. */
  code: string;
  /** Academic year as printed, e.g. "2024-25". */
  year: string;
  companies: number | null;
  inCampus: number | null;
  offCampus: number | null;
  /** inCampus + offCampus, where both are present. */
  total: number | null;
  /**
   * Highest package in LPA. The source column is inconsistent — most
   * departments print a number of lakhs ("8", "9 LPA") but Civil and Textile
   * print rupees ("360000"), so anything above 1000 is divided down.
   */
  highestLpa: number | null;
  /** The cell exactly as printed, since the normalisation above is ours. */
  highestRaw: string;
}

const num = (s?: string): number | null => {
  if (!s) return null;
  const n = Number(String(s).replace(/[^0-9.]/g, ""));
  return Number.isFinite(n) ? n : null;
};

/**
 * The year-by-year tables from the departments that publish one, newest first
 * within each department. Company-wise tables (DS, CB) are skipped — they are
 * a different shape and are already rendered on the department page itself.
 */
export function deptPlacementHistory(
  departments: Array<{ code: string; placement?: { headers: string[]; rows: string[][] } }>,
): DeptPlacementYear[] {
  const out: DeptPlacementYear[] = [];
  for (const d of departments) {
    if (!d.placement) continue;
    // Company-wise tables lead with "Sl.No."; year-wise ones with "Academic…".
    if (!/^academic/i.test(d.placement.headers[0] ?? "")) continue;
    for (const r of d.placement.rows) {
      const inC = num(r[2]);
      const off = num(r[3]);
      const hi = num(r[4]);
      out.push({
        code: d.code,
        year: r[0],
        companies: num(r[1]),
        inCampus: inC,
        offCampus: off,
        total: inC === null && off === null ? null : (inC ?? 0) + (off ?? 0),
        highestLpa: hi === null ? null : hi > 1000 ? +(hi / 100000).toFixed(2) : hi,
        highestRaw: r[4] ?? "",
      });
    }
  }
  return out;
}

/**
 * The academic year an entry belongs to, as a single number: "2023-24" counts
 * as 2024, the year that cohort left. Batch rows already print one ("2026") and
 * pass straight through.
 */
export function academicYearEnd(printed: string): number | null {
  const m = printed.match(/^(\d{4})\s*[-–]\s*(\d{2,4})$/);
  if (!m) {
    const n = Number(printed.trim());
    return Number.isFinite(n) && n > 1900 ? n : null;
  }
  const start = Number(m[1]);
  const tail = m[2];
  if (tail.length === 4) return Number(tail);
  // Guard the century rollover, where "1999-00" would otherwise land on 1900.
  let end = Math.floor(start / 100) * 100 + Number(tail);
  if (end < start) end += 100;
  return end;
}

export interface DeptPlacementRow {
  /** Single year: the batch year, or the year an academic year ends in. */
  year: number;
  /** As the source prints it — "2026" from the sheet, "2023-24" from the department. */
  printedAs: string;
  /**
   * Which record the row came from. The two count different things: the sheet
   * counts OFFERS made to a graduating batch, the department counts STUDENTS
   * placed in an academic year, and one student may hold more than one offer.
   * They sit in one series because a reader wants one line per year, but the
   * basis stays on the row so a caller can say which is which.
   */
  basis: "batch" | "academic";
  companies: number | null;
  inCampus: number | null;
  /**
   * The sheet does not separate on- from off-campus, so batch rows carry 0
   * rather than a figure the record does not hold.
   */
  offCampus: number | null;
  total: number | null;
  highestLpa: number | null;
  highestRaw: string;
}

/**
 * One placement series for a department, newest year first: the T&P Cell's
 * batch rows on top of the department's own academic-year table.
 *
 * The two cannot collide today — the academic-year rows covering the sheet's
 * period (2024-25, 2025-26) were dropped from departments.ts when the batch
 * tables landed, so the newest academic row ends in 2024 and the batch rows
 * start at 2025. The dedupe below keeps the batch row if that ever changes.
 */
export function deptPlacementSeries(d: {
  placementBatches?: { headers: string[]; rows: string[][] };
  placement?: { headers: string[]; rows: string[][] };
}): DeptPlacementRow[] {
  const out: DeptPlacementRow[] = [];

  for (const r of d.placementBatches?.rows ?? []) {
    const year = academicYearEnd(r[0] ?? "");
    if (year === null) continue;
    const offers = num(r[2]);
    out.push({
      year,
      printedAs: r[0] ?? "",
      basis: "batch",
      companies: num(r[1]),
      inCampus: offers,
      offCampus: 0,
      total: offers,
      highestLpa: num(r[3]),
      highestRaw: r[3] ?? "",
    });
  }

  // Company-wise tables (DS, CB) lead with "Sl.No." and are a different
  // shape; they stay on the page as their own table.
  if (/^academic/i.test(d.placement?.headers[0] ?? "")) {
    for (const r of d.placement!.rows) {
      const year = academicYearEnd(r[0] ?? "");
      if (year === null) continue;
      const inC = num(r[2]);
      const off = num(r[3]);
      const hi = num(r[4]);
      out.push({
        year,
        printedAs: r[0] ?? "",
        basis: "academic",
        companies: num(r[1]),
        inCampus: inC,
        offCampus: off,
        total: inC === null && off === null ? null : (inC ?? 0) + (off ?? 0),
        highestLpa: hi === null ? null : hi > 1000 ? +(hi / 100000).toFixed(2) : hi,
        highestRaw: r[4] ?? "",
      });
    }
  }

  const seen = new Set<number>();
  return out
    .sort((a, b) => b.year - a.year || (a.basis === "batch" ? -1 : 1))
    .filter((r) => (seen.has(r.year) ? false : (seen.add(r.year), true)));
}
